import { css } from 'lit';

export default css`
  :host {
    /* Two visual variants, selected by the reflected variant attribute (see
     * tab.styles.ts for the tab side):
     *
     * classic (default) - filled folder tabs welded to the content surface;
     * the pill (indicator) is hidden by the component (the active tab's fill
     * is the state).
     *
     * chip - outlined chips on the page background with a flat primary pill
     * gliding behind the active tab and the content surface as a rounded
     * card a step below the strip.
     *
     * --indicator-color themes the pill; --rail-padding is an opt-in inset
     * around the strip (also offsets the pill).
     *
     * The pill default follows the host app's environment tint
     * (--overwrite-main, set on <body> by the dev/test stylesheets) exactly
     * like the tab fills and the table2 header do, falling back to the
     * design-system primary. */
    --indicator-color: var(--overwrite-main, var(--webmodule-color-primary-600));
    --rail-padding: 0px;

    display: block;
  }

  .tab-group {
    display: flex;
    border-radius: 0;
    /* fill the host when it is stretched (resolves to auto when it is not) */
    height: 100%;
  }

  .tab-group__tabs {
    display: flex;
    position: relative;
    gap: var(--webmodule-spacing-3x-small);
    padding: var(--rail-padding);
  }

  :host([variant='chip']) .tab-group__tabs {
    gap: var(--webmodule-spacing-2x-small);
  }

  /* keep the slotted tabs painting above the absolutely positioned pill */
  .tab-group__tabs ::slotted(webmodule-tab) {
    position: relative;
    z-index: 1;
  }

  /* chips round all corners; the classic folder corners are placement-aware
   * and set per placement below */
  :host([variant='chip']) .tab-group__tabs ::slotted(webmodule-tab) {
    --tab-border-radius: var(--webmodule-border-radius-medium);
  }

  .tab-group__indicator {
    position: absolute;
    background-color: var(--indicator-color);
    border-radius: var(--webmodule-border-radius-medium);
    transition:
      var(--webmodule-animate-fast) translate ease,
      var(--webmodule-animate-fast) width ease,
      var(--webmodule-animate-fast) height ease;
  }

  .tab-group--has-scroll-controls .tab-group__nav-container {
    position: relative;
    padding: 0 var(--webmodule-spacing-x-large);
  }

  .tab-group__body {
    display: block;
    overflow: auto;
    /* the content surface: panels sit on a solid sheet the tabs connect to */
    background-color: var(--webmodule-color-neutral-0);
  }

  :host([variant='chip']) .tab-group__body {
    border-radius: var(--webmodule-border-radius-large);
  }

  .tab-group__scroll-button {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    bottom: 0;
    width: var(--webmodule-spacing-x-large);
  }

  .tab-group__scroll-button--start {
    left: 0;
  }

  .tab-group__scroll-button--end {
    right: 0;
  }

  /*
   * Top
   */

  .tab-group--top {
    flex-direction: column;
  }

  .tab-group--top .tab-group__nav-container {
    order: 1;
  }

  :host([variant='chip']) .tab-group--top .tab-group__nav-container {
    margin-block-end: var(--webmodule-spacing-x-small);
  }

  .tab-group--top .tab-group__nav {
    display: flex;
    overflow-x: auto;
    /* keep the strip's min-content width from propagating up and widening the
     * page; with the width pinned to the container the strip scrolls and the
     * chevron scroll buttons appear instead */
    contain: inline-size;

    /* Hide scrollbar in Firefox */
    scrollbar-width: none;
  }

  /* Hide scrollbar in Chrome/Safari */

  .tab-group--top .tab-group__nav::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  .tab-group--top .tab-group__tabs {
    /* the strip hugs its tabs instead of spanning the full width */
    flex: 0 0 auto;
    flex-direction: row;
  }

  .tab-group--top .tab-group__tabs ::slotted(webmodule-tab) {
    --tab-border-radius: var(--webmodule-border-radius-medium) var(--webmodule-border-radius-medium) 0 0;
  }

  .tab-group--top .tab-group__indicator {
    top: var(--rail-padding);
    bottom: var(--rail-padding);
    left: var(--rail-padding);
  }

  .tab-group--top .tab-group__body {
    order: 2;
    flex: 1 1 auto;
    min-height: 0;
  }

  .tab-group--top ::slotted(webmodule-tab-panel) {
    --padding: var(--webmodule-spacing-medium);
  }

  /*
   * Bottom
   */

  .tab-group--bottom {
    flex-direction: column;
  }

  .tab-group--bottom .tab-group__nav-container {
    order: 2;
  }

  :host([variant='chip']) .tab-group--bottom .tab-group__nav-container {
    margin-block-start: var(--webmodule-spacing-x-small);
  }

  .tab-group--bottom .tab-group__nav {
    display: flex;
    overflow-x: auto;
    /* see the top placement note: pin the width so the strip scrolls */
    contain: inline-size;

    /* Hide scrollbar in Firefox */
    scrollbar-width: none;
  }

  /* Hide scrollbar in Chrome/Safari */

  .tab-group--bottom .tab-group__nav::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  .tab-group--bottom .tab-group__tabs {
    flex: 0 0 auto;
    flex-direction: row;
  }

  .tab-group--bottom .tab-group__tabs ::slotted(webmodule-tab) {
    --tab-border-radius: 0 0 var(--webmodule-border-radius-medium) var(--webmodule-border-radius-medium);
  }

  .tab-group--bottom .tab-group__indicator {
    top: var(--rail-padding);
    bottom: var(--rail-padding);
    left: var(--rail-padding);
  }

  .tab-group--bottom .tab-group__body {
    order: 1;
    flex: 1 1 auto;
    min-height: 0;
  }

  .tab-group--bottom ::slotted(webmodule-tab-panel) {
    --padding: var(--webmodule-spacing-medium);
  }

  /*
   * Start
   */

  .tab-group--start {
    flex-direction: row;
  }

  .tab-group--start .tab-group__nav-container {
    order: 1;
    /* a definite height (100% of the group, resolving to auto when the host is
     * not stretched) so the nav below can scroll an overlong tab list instead
     * of flowing over what is beneath the group */
    height: 100%;
  }

  :host([variant='chip']) .tab-group--start .tab-group__nav-container {
    margin-inline-end: var(--webmodule-spacing-x-small);
  }

  .tab-group--start .tab-group__nav {
    max-height: 100%;
    overflow-y: auto;
    scrollbar-width: thin;
  }

  .tab-group--start .tab-group__tabs {
    flex: 0 0 auto;
    flex-direction: column;
  }

  .tab-group--start .tab-group__tabs ::slotted(webmodule-tab) {
    --tab-border-radius: var(--webmodule-border-radius-medium) 0 0 var(--webmodule-border-radius-medium);
  }

  .tab-group--start .tab-group__indicator {
    top: var(--rail-padding);
    left: var(--rail-padding);
    right: var(--rail-padding);
  }

  .tab-group--start .tab-group__body {
    flex: 1 1 auto;
    min-width: 0;
    order: 2;
  }

  .tab-group--start ::slotted(webmodule-tab-panel) {
    --padding: var(--webmodule-spacing-medium);
  }

  /*
   * End
   */

  .tab-group--end {
    flex-direction: row;
  }

  .tab-group--end .tab-group__nav-container {
    order: 2;
    height: 100%;
  }

  :host([variant='chip']) .tab-group--end .tab-group__nav-container {
    margin-inline-start: var(--webmodule-spacing-x-small);
  }

  .tab-group--end .tab-group__nav {
    max-height: 100%;
    overflow-y: auto;
    scrollbar-width: thin;
  }

  .tab-group--end .tab-group__tabs {
    flex: 0 0 auto;
    flex-direction: column;
  }

  .tab-group--end .tab-group__tabs ::slotted(webmodule-tab) {
    --tab-border-radius: 0 var(--webmodule-border-radius-medium) var(--webmodule-border-radius-medium) 0;
  }

  .tab-group--end .tab-group__indicator {
    top: var(--rail-padding);
    left: var(--rail-padding);
    right: var(--rail-padding);
  }

  .tab-group--end .tab-group__body {
    flex: 1 1 auto;
    min-width: 0;
    order: 1;
  }

  .tab-group--end ::slotted(webmodule-tab-panel) {
    --padding: var(--webmodule-spacing-medium);
  }
`;
